import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

type Slide =
  | { type: "video"; src: string; poster?: string }
  | { type: "image"; src: string };

const RecentlySoldSection: React.FC = () => {
  const { t } = useTranslation();
  const titleParts = t("recentlySold.title").split(
    /(DEM|properties|propriétés|propiedades)/gi
  );

  // --- MEDIA (KEEP EXTENSIONS EXACT) ---
  const portraits = useMemo(
    () => ["/sold/A.JPG", "/sold/B.JPG", "/sold/C.webp", "/sold/D.webp"],
    []
  );

  const landscapes = useMemo(
    () => Array.from({ length: 23 }, (_, i) => `/sold/${i + 1}.webp`),
    []
  );

  const videoSrc = "/sold/video.MP4";
  const videoPoster = "/sold/video-poster.webp";

  // --- ONE SLIDER LIST ---
  const slides: Slide[] = useMemo(
    () => [
      { type: "video", src: videoSrc, poster: videoPoster },
      ...portraits.map((src) => ({ type: "image" as const, src })),
      ...landscapes.map((src) => ({ type: "image" as const, src })),
    ],
    [portraits, landscapes]
  );

  // --- LIGHTBOX ---
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(4);

  const openLightbox = useCallback((index: number) => {
    setActiveIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const next = useCallback(() => {
    setActiveIndex((i) => (i + 1) % slides.length);
  }, [slides.length]);

  const prev = useCallback(() => {
    setActiveIndex((i) => (i - 1 + slides.length) % slides.length);
  }, [slides.length]);

  // --- keyboard + lock scroll ---
  useEffect(() => {
    if (!lightboxOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };

    window.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [lightboxOpen, closeLightbox, next, prev]);

  // --- swipe mobile in lightbox ---
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const diff = touchStartX.current - touchEndX.current;
    if (diff > 60) next();
    if (diff < -60) prev();
  };

  const gallerySlides = useMemo(
    () => [
      ...portraits.map((src, index) => ({
        slide: { type: "image" as const, src },
        slideIndex: index + 1,
        orientation: "portrait" as const,
      })),
      ...landscapes.slice(2).map((src, index) => ({
        slide: { type: "image" as const, src },
        slideIndex: index + 7,
        orientation: "landscape" as const,
      })),
    ],
    [portraits, landscapes]
  );

  const visibleGallerySlides = gallerySlides.slice(0, visibleCount);
  const allVisible = visibleCount >= gallerySlides.length;

  const toggleGallery = () => {
    if (allVisible) {
      setVisibleCount(4);
      document.getElementById("recently-sold-gallery")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return;
    }

    setVisibleCount((count) => Math.min(count + 6, gallerySlides.length));
  };

  return (
    <>
      <section className="relative py-28 sm:py-36 lg:py-44 px-6 sm:px-8 lg:px-12 bg-[#F8F5EF] overflow-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Editorial header */}
          <div className="flex items-center gap-5 mb-14 sm:mb-20 max-w-xl">
            <span className="font-oswald text-[11px] sm:text-xs uppercase tracking-[0.34em] text-[#86523d] whitespace-nowrap">
              {t("recentlySold.eyebrow")}
            </span>
            <span className="h-px flex-1 bg-stone-300/80" aria-hidden="true" />
          </div>

          <motion.header
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end mb-16"
          >
            <h2 className="lg:col-span-8 font-oswald font-medium uppercase tracking-[-0.035em] text-[clamp(2.7rem,6vw,5.5rem)] text-stone-900 leading-[0.96]">
              {titleParts.map((part, index) =>
                /^(DEM|properties|propriétés|propiedades)$/i.test(part) ? (
                  <span
                    key={`${part}-${index}`}
                    className="text-transparent bg-clip-text bg-gradient-to-r from-[#A47C3B] via-[#a26850] to-[#6F5327]"
                  >
                    {part}
                  </span>
                ) : (
                  <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
                )
              )}
            </h2>
            <p className="lg:col-span-4 lg:pb-2 font-playfair text-lg sm:text-xl leading-relaxed text-stone-600">
              {t("recentlySold.subtitle")}
            </p>
          </motion.header>

          {/* Native-ratio lead composition: 9:16 video + two 1080:566 images */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-[0.62fr_1fr] gap-4 sm:gap-6 items-start"
          >
            <button
              type="button"
              onClick={() => openLightbox(0)}
              className="group relative w-full aspect-[9/16] overflow-hidden bg-stone-900 text-left"
              aria-label={t("recentlySold.videoTitle")}
            >
              <img
                className="absolute inset-0 w-full h-full object-cover"
                src={videoPoster}
                alt=""
                width={720}
                height={1280}
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-black/5" />
              <div className="absolute left-6 right-6 bottom-6">
                <span className="font-oswald text-[10px] uppercase tracking-[0.3em] text-white/60">
                  {t("recentlySold.videoBadge")}
                </span>
                <p className="mt-2 font-playfair text-xl sm:text-2xl text-white">
                  {t("recentlySold.videoTitle")}
                </p>
              </div>
            </button>

            <div className="grid gap-4 sm:gap-6">
              {landscapes.slice(0, 2).map((src, index) => (
                <button
                  type="button"
                  key={src}
                  onClick={() => openLightbox(index + 5)}
                  className="group relative w-full aspect-[1080/566] overflow-hidden bg-stone-100"
                >
                  <img
                    src={src}
                    alt={`${t("recentlySold.sold")} ${index + 1}`}
                    className="absolute inset-0 w-full h-full object-contain bg-stone-100 transition-transform duration-700 group-hover:scale-[1.015]"
                    loading="lazy"
                    width={1080}
                    height={566}
                    decoding="async"
                  />
                </button>
              ))}
            </div>
          </motion.div>

          {/* Expandable native-ratio gallery */}
          <div id="recently-sold-gallery" className="scroll-mt-28 mt-4 sm:mt-6">
            <motion.div layout className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              <AnimatePresence initial={false}>
                {visibleGallerySlides.map(({ slide, slideIndex, orientation }) => (
                  <motion.button
                    layout
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    type="button"
                    key={slide.src}
                    onClick={() => openLightbox(slideIndex)}
                    className={`group relative overflow-hidden bg-stone-100 ${
                      orientation === "portrait"
                        ? "col-span-1 aspect-[4/5]"
                        : "col-span-2 aspect-[1080/566]"
                    }`}
                  >
                    <img
                      src={slide.src}
                      width={1080}
                      height={orientation === "portrait" ? 1350 : 566}
                      alt={`${t("recentlySold.sold")} ${slideIndex}`}
                      className="absolute inset-0 w-full h-full object-contain bg-stone-100 transition-transform duration-700 group-hover:scale-[1.015]"
                      loading="lazy"
                      decoding="async"
                    />
                  </motion.button>
                ))}
              </AnimatePresence>
            </motion.div>
          </div>

          <div className="mt-12 sm:mt-16 flex flex-col items-center gap-4">
            <button
              type="button"
              onClick={toggleGallery}
              className="group inline-flex items-center gap-4 px-8 py-4 border border-stone-300 hover:border-[#a26850] font-oswald text-[11px] uppercase tracking-[0.28em] text-stone-700 hover:text-[#86523d] transition-colors duration-300"
            >
              {allVisible ? t("recentlySold.showLess") : t("recentlySold.showMore")}
              <span
                className={`text-base leading-none transition-transform duration-300 ${
                  allVisible ? "rotate-45" : ""
                }`}
                aria-hidden="true"
              >
                +
              </span>
            </button>
            {!allVisible && (
              <span className="font-playfair italic text-sm text-stone-600">
                {gallerySlides.length - visibleCount} {t("recentlySold.remaining")}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] bg-black/95"
          >
            {/* click outside closes */}
            <div className="absolute inset-0 z-0" onClick={closeLightbox} />

            {/* top bar */}
            <div className="absolute top-0 left-0 right-0 px-5 py-5 flex items-center justify-between z-20">
              <div className="text-white/50 font-oswald text-[10px] tracking-[0.35em] uppercase">
                {activeIndex + 1} / {slides.length}
              </div>

              <button
                onClick={closeLightbox}
                type="button"
                aria-label="Close"
                className="w-12 h-12 rounded-full bg-white/10 border border-white/15 text-white flex items-center justify-center hover:bg-[#a26850] transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* content */}
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              className="relative z-10 w-full h-full flex items-center justify-center px-4 md:px-12"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div className="relative w-full max-w-6xl">
                <div className="w-full h-[72vh] md:h-[82vh] flex items-center justify-center">
                  {slides[activeIndex].type === "video" ? (
                    <video
                      key={slides[activeIndex].src}
                      className="max-h-full max-w-full object-contain rounded-2xl shadow-2xl"
                      src={slides[activeIndex].src}
                      controls
                      autoPlay
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img
                      key={slides[activeIndex].src}
                      src={slides[activeIndex].src}
                      alt={`Sold ${activeIndex + 1}`}
                      className="max-h-full max-w-full object-contain rounded-2xl shadow-2xl"
                      loading="eager"
                      decoding="async"
                    />
                  )}
                </div>

                {/* arrows */}
                {slides.length > 1 && (
                  <>
                    <button
                      onClick={prev}
                      type="button"
                      aria-label="Previous"
                      className="absolute -left-2 md:-left-20 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-[#a26850] transition-all z-20"
                    >
                      <ChevronLeft size={24} />
                    </button>

                    <button
                      onClick={next}
                      type="button"
                      aria-label="Next"
                      className="absolute -right-2 md:-right-20 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-[#a26850] transition-all z-20"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default RecentlySoldSection;
